import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import Spinner from './Spinner';

/**
 * ProtectedRoute
 *
 * Props:
 *   authLoading  {boolean}   — true while App.js is still resolving the auth/role state
 *   role         {string}    — current user's role ('', 'open access user', 'educator', 'moderator', 'admin')
 *   allowedRoles {string[]}  — if provided, user's role must be in this list
 *   requireAuth  {boolean}   — if true, any real (non-anonymous) role grants access
 *   children     {ReactNode}
 */
function ProtectedRoute({ authLoading, role, allowedRoles, requireAuth, children }) {
  const location = useLocation();

  // Wait for the first auth/role resolution before making any access decision
  if (authLoading) {
    return <Spinner text="Loading…" className="min-h-[60vh]" />;
  }

  // "signed in" means a real role is present (anonymous users have role === '')
  const isSignedIn = Boolean(role);

  // requireAuth — just needs to be signed in (any role)
  if (requireAuth && !isSignedIn) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  // allowedRoles — role must be in the whitelist
  if (allowedRoles && !allowedRoles.includes(role)) {
    // Not signed in at all → send to login
    if (!isSignedIn) {
      return <Navigate to="/sign-in" state={{ from: location }} replace />;
    }

    // Signed in but wrong role → Access Denied screen
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 px-4 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 text-red-600">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-8 w-8"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
            />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-800">Access Denied</h1>
        <p className="text-gray-500 max-w-sm">
          You don't have permission to view this page. Contact an administrator if you believe this is an error.
        </p>
        <a
          href="/#/"
          className="inline-flex items-center gap-2 bg-brand-600 text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-brand-700 transition-colors text-sm"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 16l-4-4m0 0l4-4m-4 4h18" />
          </svg>
          Back to Home
        </a>
      </div>
    );
  }

  return children;
}

export default ProtectedRoute;
